import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Agency, Workspace } from '@/lib/api';

interface AppState {
  currentAgency: Agency | null;
  currentWorkspace: Workspace | null;
  sidebarCollapsed: boolean;
  /** Off-canvas navigation on small screens. Not persisted. */
  mobileNavOpen: boolean;

  setCurrentAgency: (agency: Agency | null) => void;
  setCurrentWorkspace: (workspace: Workspace | null) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setMobileNavOpen: (open: boolean) => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      currentAgency: null,
      currentWorkspace: null,
      sidebarCollapsed: false,
      mobileNavOpen: false,

      // Refreshing the same agency (e.g. after saving its settings) keeps the
      // selected workspace; only a real agency switch clears it.
      setCurrentAgency: (agency) =>
        set((s) => ({
          currentAgency: agency,
          currentWorkspace:
            agency && s.currentAgency?.id === agency.id ? s.currentWorkspace : null,
        })),
      setCurrentWorkspace: (workspace) =>
        set({ currentWorkspace: workspace }),
      setSidebarCollapsed: (collapsed) =>
        set({ sidebarCollapsed: collapsed }),
      setMobileNavOpen: (open) => set({ mobileNavOpen: open }),
    }),
    {
      name: 'enflux-app-store',
      partialize: (state) => ({
        currentAgency: state.currentAgency,
        currentWorkspace: state.currentWorkspace,
        sidebarCollapsed: state.sidebarCollapsed,
      }),
    }
  )
);
